import React, { useState, useEffect } from 'react';
import Layout from '../components/dashboard/Layout';
import EditMarketingPersonModal from '../components/EditMarketingPersonModal';
import { marketingAPI, clientsAPI } from '../services/api';
import { 
  Trophy, 
  TrendingUp, 
  Upload, 
  Users,
  DollarSign,
  Target,
  Award,
  Flame,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Image as ImageIcon,
  Edit,
  DollarSign as Money
} from 'lucide-react';

const MarketingPerformancePage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [teamProgress, setTeamProgress] = useState([]);
  const [topAds, setTopAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [clients, setClients] = useState([]);
  const [marketingUsers, setMarketingUsers] = useState([]);

  // Upload Ad Form
  const [adForm, setAdForm] = useState({
    platform: 'instagram',
    ad_link: '',
    screenshot_url: '',
    likes: 0,
    comments: 0,
    shares: 0,
    views: 0
  });

  // Assign Client Form
  const [assignForm, setAssignForm] = useState({
    client_id: '',
    marketing_person_id: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [leaderboardRes, teamRes, adsRes, clientsRes] = await Promise.all([
        marketingAPI.getLeaderboard(),
        marketingAPI.getTeamProgress(),
        marketingAPI.getTopAds(10),
        clientsAPI.getAll()
      ]);
      
      setLeaderboard(leaderboardRes.data);
      setTeamProgress(teamRes.data);
      setTopAds(adsRes.data);
      setClients(clientsRes.data);
      
      // Extract marketing users from team progress
      setMarketingUsers(teamRes.data.map(t => ({
        id: t.marketing_person_id,
        name: t.marketing_person_name
      })));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadAd = async (e) => {
    e.preventDefault();
    try {
      await marketingAPI.uploadAd(adForm);
      alert('Iklan berhasil diupload!');
      setShowUploadModal(false);
      setAdForm({
        platform: 'instagram',
        ad_link: '',
        screenshot_url: '',
        likes: 0,
        comments: 0,
        shares: 0,
        views: 0
      });
      await loadData();
    } catch (error) {
      console.error('Failed to upload ad:', error);
      alert('Gagal upload iklan: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAssignClient = async (e) => {
    e.preventDefault();
    try {
      await marketingAPI.assignClient(assignForm);
      alert('Client berhasil di-assign!');
      setShowAssignModal(false);
      setAssignForm({
        client_id: '',
        marketing_person_id: '',
        notes: ''
      });
      await loadData();
    } catch (error) {
      console.error('Failed to assign client:', error);
      alert('Gagal assign client: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditPerson = (person) => {
    setSelectedPerson(person);
    setShowEditModal(true);
  };

  const handleSavePerson = async (personId, data) => {
    try {
      await marketingAPI.updatePerson(personId, data);
      alert('✅ Data marketing person berhasil diupdate!');
      setShowEditModal(false);
      setSelectedPerson(null);
      await loadData();
    } catch (error) {
      console.error('Failed to update person:', error);
      alert('❌ Gagal update: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeletePerson = async (personId) => {
    try {
      await marketingAPI.deletePerson(personId);
      alert('✅ Marketing person berhasil dihapus (set inactive)');
      setShowEditModal(false);
      setSelectedPerson(null);
      await loadData();
    } catch (error) {
      console.error('Failed to delete person:', error);
      alert('❌ Gagal delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: <Trophy className="w-6 h-6 text-yellow-500" />, color: 'bg-yellow-500/10 border-yellow-500/30' };
    if (rank === 2) return { icon: <Award className="w-6 h-6 text-gray-400" />, color: 'bg-gray-400/10 border-gray-400/30' };
    if (rank === 3) return { icon: <Award className="w-6 h-6 text-orange-600" />, color: 'bg-orange-600/10 border-orange-600/30' };
    return { icon: <Target className="w-5 h-5 text-muted-foreground" />, color: 'bg-muted/50 border-border' };
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: '📷',
      tiktok: '🎵',
      facebook: '👥',
      youtube: '▶️'
    };
    return icons[platform] || '📱';
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading marketing performance...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">🏆 Marketing Performance & Rewards</h1>
            <p className="text-muted-foreground">Dashboard komisi & leaderboard untuk 30 anggota tim marketing</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Iklan</span>
            </button>
            <button
              onClick={() => setShowAssignModal(true)}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors flex items-center space-x-2"
            >
              <Users className="w-4 h-4" />
              <span>Assign Client</span>
            </button>
          </div>
        </div>

        {/* Leaderboard - PRIORITY #1 */}
        <div className="glass-card p-6 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="flex items-center space-x-2 mb-6">
            <Trophy className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold text-foreground">Leaderboard Komisi (8% dari Invoice Pertama)</h2>
          </div>

          {leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-medium">Belum ada komisi tercatat</p>
              <p className="text-sm text-muted-foreground mt-1">Assign client ke marketing person untuk mulai tracking komisi</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((person) => {
                const badge = getRankBadge(person.rank);
                return (
                  <div
                    key={person.marketing_person_id}
                    className={`p-5 rounded-xl border-2 ${badge.color} transition-all hover:shadow-lg`}
                  >
                    <div className="flex items-center justify-between">
                      {/* Left Side - Rank & Name */}
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          {badge.icon}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="text-lg font-bold text-foreground">#{person.rank} {person.marketing_person_name}</p>
                            {person.rank <= 3 && (
                              <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                                TOP PERFORMER
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                            <span>👥 {person.total_clients} clients</span>
                            <span>📄 {person.total_invoices} invoices</span>
                          </div>
                        </div>
                      </div>

                      {/* Right Side - Commission & Actions */}
                      <div className="text-right space-y-2">
                        <p className="text-2xl font-bold text-primary">
                          Rp {(person.total_commission / 1000).toLocaleString('id-ID')}K
                        </p>
                        <div className="mt-1 text-sm space-y-1">
                          <p className="text-secondary">
                            ✓ Dibayar: Rp {(person.paid_commission / 1000).toLocaleString('id-ID')}K
                          </p>
                          <p className="text-destructive">
                            ⏳ Pending: Rp {(person.pending_commission / 1000).toLocaleString('id-ID')}K
                          </p>
                        </div>
                        <button
                          onClick={() => handleEditPerson(person)}
                          className="mt-2 px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-lg hover:bg-primary/20 transition-colors flex items-center space-x-1 ml-auto"
                        >
                          <Edit className="w-3 h-3" />
                          <span>Edit</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Daily Upload Progress - Team */}
        <div className="glass-card p-6 rounded-xl border border-border/40">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-secondary" />
              <h2 className="text-xl font-bold text-foreground">Progress Upload Iklan Harian (Target: 16/hari)</h2>
            </div>
            <p className="text-sm text-muted-foreground">4 IG + 4 TikTok + 4 FB + 4 YouTube</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamProgress.map((person) => (
              <div
                key={person.marketing_person_id}
                className={`p-4 rounded-lg border-2 ${
                  person.completed 
                    ? 'bg-secondary/5 border-secondary/30' 
                    : person.completion_rate > 50 
                      ? 'bg-primary/5 border-primary/30'
                      : 'bg-destructive/5 border-destructive/30'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-foreground">{person.marketing_person_name}</p>
                  {person.completed ? (
                    <CheckCircle className="w-5 h-5 text-secondary" />
                  ) : person.completion_rate > 0 ? (
                    <AlertCircle className="w-5 h-5 text-primary" />
                  ) : (
                    <XCircle className="w-5 h-5 text-destructive" />
                  )}
                </div>

                <div className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium text-foreground">
                      {person.total_uploaded}/{person.total_target} ({person.completion_rate.toFixed(0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        person.completed ? 'bg-secondary' : person.completion_rate > 50 ? 'bg-primary' : 'bg-destructive'
                      }`}
                      style={{ width: `${Math.min(person.completion_rate, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 text-xs mb-3">
                  {Object.entries(person.platform_counts).map(([platform, count]) => (
                    <div key={platform} className="text-center">
                      <div className="text-lg mb-1">{getPlatformIcon(platform)}</div>
                      <div className={`font-semibold ${count >= 4 ? 'text-secondary' : 'text-muted-foreground'}`}>
                        {count}/4
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleEditPerson(person)}
                  className="w-full px-3 py-1.5 bg-muted text-foreground text-xs font-medium rounded-lg hover:bg-muted/80 transition-colors flex items-center justify-center space-x-1"
                >
                  <Edit className="w-3 h-3" />
                  <span>Edit Person</span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performing Ads */}
        <div className="glass-card p-6 rounded-xl border border-border/40">
          <div className="flex items-center space-x-2 mb-6">
            <Flame className="w-5 h-5 text-orange-500" />
            <h2 className="text-xl font-bold text-foreground">Iklan Paling Hype (30 Hari Terakhir)</h2>
          </div>

          {topAds.length === 0 ? (
            <div className="text-center py-8">
              <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-medium">Belum ada iklan terupload</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {topAds.map((ad, index) => (
                <div
                  key={ad.id}
                  className="p-4 rounded-lg border border-border bg-background/50 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-2xl">{getPlatformIcon(ad.platform)}</span>
                        <div>
                          <p className="font-semibold text-foreground capitalize">{ad.platform}</p>
                          <p className="text-sm text-muted-foreground">{ad.marketing_person_name}</p>
                        </div>
                      </div>
                      <a
                        href={ad.ad_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline block truncate"
                      >
                        {ad.ad_link}
                      </a>
                    </div>
                    <div className="flex items-center space-x-1 text-orange-500 font-bold">
                      <Flame className="w-4 h-4" />
                      <span>{ad.engagement_score}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 text-center text-sm">
                    <div>
                      <p className="text-muted-foreground">❤️</p>
                      <p className="font-semibold text-foreground">{ad.likes}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">💬</p>
                      <p className="font-semibold text-foreground">{ad.comments}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">🔄</p>
                      <p className="font-semibold text-foreground">{ad.shares}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">👁️</p>
                      <p className="font-semibold text-foreground">{ad.views}</p>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(ad.upload_date).toLocaleDateString('id-ID')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upload Ad Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card rounded-xl p-6 w-full max-w-lg">
              <h3 className="text-xl font-bold text-foreground mb-4">Upload Iklan Harian</h3>
              
              <form onSubmit={handleUploadAd} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Platform</label>
                  <select
                    value={adForm.platform}
                    onChange={(e) => setAdForm({ ...adForm, platform: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                    required
                  >
                    <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="facebook">Facebook</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Link Iklan *</label>
                  <input
                    type="url"
                    value={adForm.ad_link}
                    onChange={(e) => setAdForm({ ...adForm, ad_link: e.target.value })}
                    placeholder="https://instagram.com/p/..."
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Screenshot URL (opsional)</label>
                  <input
                    type="url"
                    value={adForm.screenshot_url}
                    onChange={(e) => setAdForm({ ...adForm, screenshot_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Likes</label>
                    <input
                      type="number"
                      value={adForm.likes}
                      onChange={(e) => setAdForm({ ...adForm, likes: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Comments</label>
                    <input
                      type="number"
                      value={adForm.comments}
                      onChange={(e) => setAdForm({ ...adForm, comments: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Shares</label>
                    <input
                      type="number"
                      value={adForm.shares}
                      onChange={(e) => setAdForm({ ...adForm, shares: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Views</label>
                    <input
                      type="number"
                      value={adForm.views}
                      onChange={(e) => setAdForm({ ...adForm, views: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Upload
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Assign Client Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card rounded-xl p-6 w-full max-w-lg">
              <h3 className="text-xl font-bold text-foreground mb-4">Assign Client ke Marketing Person</h3>
              
              <form onSubmit={handleAssignClient} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Client *</label>
                  <select
                    value={assignForm.client_id}
                    onChange={(e) => setAssignForm({ ...assignForm, client_id: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                    required
                  >
                    <option value="">Pilih Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.company_name || client.full_name} - {client.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Marketing Person *</label>
                  <select
                    value={assignForm.marketing_person_id}
                    onChange={(e) => setAssignForm({ ...assignForm, marketing_person_id: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                    required
                  >
                    <option value="">Pilih Marketing Person</option>
                    {marketingUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Catatan (opsional)</label>
                  <textarea
                    value={assignForm.notes}
                    onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                    rows="3"
                    placeholder="Catatan tentang client ini..."
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary/50 text-foreground"
                  />
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    <strong>Info:</strong> Marketing person akan mendapat komisi 8% dari invoice pertama yang PAID dari client ini.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAssignModal(false)}
                    className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors"
                  >
                    Assign
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Marketing Person Modal */}
        <EditMarketingPersonModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPerson(null);
          }}
          person={selectedPerson}
          onSave={handleSavePerson}
          onDelete={handleDeletePerson}
        />
      </div>
    </Layout>
  );
};

export default MarketingPerformancePage;
